import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { useI18n } from '@/i18n/i18n-react'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { Label } from '@/components/ui/label'
import { useState, useMemo, useEffect } from 'react'
import { Wallet, ReceiptText, Sparkles, FileText, Check } from 'lucide-react'
import { tenant } from '@/tenants/tenant'
import { termsAccepted } from '@/utils/auth'
import { useBaseAcceptTerms } from '@/api/react-query'
import { Sentry } from '@/utils/error-handling'
import { useGetWalletUrl } from '@/hooks/wallet'
import { useAuth } from '@/providers/auth-provider'
import { buildIntroPages, sanitizeRedirect } from '@/utils/route-guards'
import { cn } from '@/lib/utils'

export const Route = createFileRoute('/onboarding/intro')({
	validateSearch: (search: Record<string, unknown>) => ({
		redirect: sanitizeRedirect(search.redirect)
	}),
	component: IntroScreen
})

function IntroScreen() {
	const { L } = useI18n()
	const navigate = useNavigate()
	const { features } = tenant
	const { redirect } = Route.useSearch()

	const [currentIndex, setCurrentIndex] = useState(0)
	const [privacyAccepted, setPrivacyAccepted] = useState(false)
	const [termsChecked, setTermsChecked] = useState(false)

	const acceptTermsMutation = useBaseAcceptTerms()
	const { customer } = useAuth()
	const showLoyaltyPages = features.loyaltyCardEnabled
	const { url: walletUrl, isLoading: walletLoading, notFound: walletNotFound } = useGetWalletUrl(showLoyaltyPages)

	const termsLastUpdatedAtMs =
		features.onboarding.style === 'fullscreen' ? new Date(features.onboarding.termsLastUpdatedAt).getTime() : 0
	const needsTerms = (customer?.termsAgreedToAt ?? 0) < termsLastUpdatedAtMs

	const pages = useMemo(
		() => buildIntroPages({ needsTerms, showLoyaltyPages, walletNotFound }),
		[needsTerms, showLoyaltyPages, walletNotFound]
	)

	useEffect(() => {
		if (pages.length === 0) {
			finish()
		}
	}, [pages.length])

	const currentPage = pages[currentIndex]
	const loyaltyPages = pages.filter(p => p !== 'TERMS')
	const loyaltyPageIndex = currentPage === 'TERMS' ? -1 : loyaltyPages.indexOf(currentPage)

	const finish = () => navigate({ to: (redirect as any) ?? '/' })

	const handleAcceptTerms = () => {
		if (!termsChecked || !privacyAccepted) return
		termsAccepted.next(true)
		try {
			// Fire and forget
			acceptTermsMutation({})
		} catch (err) {
			Sentry.captureException(err)
		}
		if (currentIndex < pages.length - 1) {
			setCurrentIndex(i => i + 1)
		} else {
			finish()
		}
	}

	const handleContinue = () => {
		if (currentIndex < pages.length - 1) {
			setCurrentIndex(i => i + 1)
		} else {
			finish()
		}
	}

	const handleAddToWallet = () => {
		if (walletUrl) {
			window.open(walletUrl, '_blank', 'noopener,noreferrer')
		}
		finish()
	}

	return (
		<div className='bg-primary flex min-h-dvh flex-col'>
			<div className='px-6 pt-8 text-center'>
				<h1 className='text-primary-foreground text-2xl font-bold'>{L.Onboarding.Continue()}</h1>
			</div>

			<div className='flex flex-1 flex-col items-center justify-center px-6 py-8'>
				{currentPage === 'TERMS' && (
					<div className='flex w-full max-w-sm flex-col gap-6'>
						<div className='flex flex-col items-center gap-4 text-center'>
							<div className='bg-primary-foreground/20 flex h-28 w-28 items-center justify-center rounded-full'>
								<FileText className='text-primary-foreground h-14 w-14' />
							</div>
							<h2 className='text-primary-foreground text-xl font-semibold'>
								{L.Onboarding.TermsTitle()}
							</h2>
							<p className='text-primary-foreground/80 text-sm'>{L.Onboarding.TermsDescription()}</p>
						</div>

						<div className='flex flex-col gap-4'>
							<div className='flex items-start gap-3'>
								<Checkbox
									id='terms'
									checked={termsChecked}
									onCheckedChange={v => setTermsChecked(Boolean(v))}
									className='border-primary-foreground mt-0.5'
									indicatorClassName='bg-primary-foreground text-primary'
								/>
								<Label htmlFor='terms' className='text-primary-foreground cursor-pointer text-sm'>
									{L.Onboarding.TermsAgreePrefix()}{' '}
									<a
										href='#'
										className='underline'
										onClick={e => {
											e.preventDefault()
										}}
									>
										{L.Onboarding.TermsAndConditions()}
									</a>
								</Label>
							</div>
							<div className='flex items-start gap-3'>
								<Checkbox
									id='privacy'
									checked={privacyAccepted}
									onCheckedChange={v => setPrivacyAccepted(Boolean(v))}
									className='border-primary-foreground mt-0.5'
									indicatorClassName='bg-primary-foreground text-primary'
								/>
								<Label htmlFor='privacy' className='text-primary-foreground cursor-pointer text-sm'>
									{L.Onboarding.PrivacyPolicyAgreePrefix()}{' '}
									<a
										href='#'
										className='underline'
										onClick={e => {
											e.preventDefault()
										}}
									>
										{L.Onboarding.PrivacyPolicy()}
									</a>
								</Label>
							</div>
						</div>
					</div>
				)}

				{currentPage === 'LOYALTY' && (
					<PageContent
						icon={<Sparkles className='text-primary-foreground h-14 w-14' />}
						title={L.Onboarding.LoyaltyTitle()}
						description={L.Onboarding.LoyaltyDescription()}
					/>
				)}

				{currentPage === 'RECEIPTS' && (
					<PageContent
						icon={<ReceiptText className='text-primary-foreground h-14 w-14' />}
						title={L.Onboarding.ReceiptsTitle()}
						description={L.Onboarding.ReceiptsDescription()}
					/>
				)}

				{currentPage === 'WALLET' && (
					<PageContent
						icon={<Wallet className='text-primary-foreground h-14 w-14' />}
						title={L.Onboarding.WalletTitle()}
						description={L.Onboarding.WalletDescription()}
					/>
				)}
			</div>

			{/* Step indicators for loyalty pages */}
			{currentPage !== 'TERMS' && loyaltyPages.length > 1 && (
				<div className='mb-4 flex justify-center gap-2'>
					{loyaltyPages.map((page, index) => (
						<div
							key={page}
							className={cn(
								'h-2 rounded-full transition-all',
								index === loyaltyPageIndex ? 'bg-primary-foreground w-8' : (
									'bg-primary-foreground/40 w-2'
								)
							)}
						/>
					))}
				</div>
			)}

			{/* Navigation buttons */}
			<div className='flex gap-3 p-6'>
				{currentPage === 'TERMS' && (
					<Button
						className='bg-primary-foreground text-primary hover:bg-primary-foreground/90 w-full disabled:opacity-50'
						onClick={handleAcceptTerms}
						disabled={!termsChecked || !privacyAccepted}
					>
						<Check className='mr-2 h-4 w-4' />
						{L.Onboarding.TermsUpdatedAccept()}
					</Button>
				)}

				{(currentPage === 'LOYALTY' || currentPage === 'RECEIPTS') && (
					<Button
						className='bg-primary-foreground text-primary hover:bg-primary-foreground/90 w-full'
						onClick={handleContinue}
					>
						{L.Onboarding.Continue()}
					</Button>
				)}

				{currentPage === 'WALLET' && (
					<>
						<Button
							variant='ghost'
							className='text-primary-foreground hover:bg-primary-foreground/10 hover:text-primary-foreground flex-1'
							onClick={finish}
						>
							{L.Onboarding.SkipAddingToWallet()}
						</Button>
						{(walletUrl || walletLoading) && (
							<Button
								className='bg-primary-foreground text-primary hover:bg-primary-foreground/90 flex-1'
								onClick={handleAddToWallet}
								isLoading={walletLoading}
								disabled={walletLoading}
							>
								<Wallet className='mr-2 h-4 w-4' />
								{L.Onboarding.AddToWallet()}
							</Button>
						)}
					</>
				)}
			</div>
		</div>
	)
}

function PageContent({ icon, title, description }: { icon: React.ReactNode; title: string; description: string }) {
	return (
		<div className='flex w-full max-w-sm flex-col items-center gap-6 text-center'>
			<div className='bg-primary-foreground/20 flex h-28 w-28 items-center justify-center rounded-full'>
				{icon}
			</div>
			<h2 className='text-primary-foreground text-xl font-semibold'>{title}</h2>
			<p className='text-primary-foreground/80 text-sm'>{description}</p>
		</div>
	)
}
